import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, PenLine } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { DraftStepFooter } from "@/components/drafting/DraftStepFooter";
import { DraftStepper } from "@/components/drafting/DraftStepper";
import { getDraftStepNav } from "@/lib/draft-step-nav";
import { DraftWizard } from "@/components/drafting-wizard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { ProgressPanel } from "@/components/results/ProgressPanel";
import {
  confirmDraftParties,
  createDraftMatter,
  deleteDraftSourceDocument,
  extractDraftParties,
  fetchDraftMatter,
  resolveDraftConflict,
  updateDraftMatter,
  uploadDraftSourceDocuments,
} from "@/lib/api/drafts";
import { encodeSourceDocuments } from "@/lib/draft-document-utils";
import { useDraftGeneration } from "@/hooks/useDraftGeneration";
import { draftMatterHasGeneratedDraft } from "@/lib/draft-matter-status";
import { darkPageCanvasBackgroundClassName } from "@/lib/page-canvas";
import { cn } from "@/lib/utils";
import type {
  DraftFactConflict,
  DraftPartyInput,
  DraftSourceDocType,
  DraftSourceDocument,
  DraftSourceDocumentInput,
  DraftWizardData,
} from "@/types/drafting";

function normalizeSourceDocType(value: string): DraftSourceDocType {
  if (
    value === "guideline" ||
    value === "playbook" ||
    value === "precedent" ||
    value === "background" ||
    value === "party-source"
  ) {
    return value;
  }
  return "background";
}

function mapPersistedSourceDocument(doc: DraftSourceDocument): DraftSourceDocumentInput {
  return {
    id: doc.id,
    docType: normalizeSourceDocType(String(doc.doc_type)),
    fileName: doc.file_name,
    persisted: true,
  };
}

const pageShellClassName = cn(
  "relative flex min-h-0 w-full flex-1 flex-col overflow-hidden",
  darkPageCanvasBackgroundClassName,
);

const defaultWizardData: DraftWizardData = {
  templateId: null,
  contractType: "",
  legalRoleId: null,
  jurisdiction: "US (Delaware)",
  title: "",
  backgroundInfo: "",
  agentInstructions: "",
  sourceDocuments: [],
  parties: [],
};

export function NewDraftPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matterIdParam = searchParams.get("matterId");
  const stepParam = Number(searchParams.get("step") || "1");

  const [currentStep, setCurrentStep] = useState(stepParam === 2 ? 2 : 1);
  const [matterId, setMatterId] = useState<string | null>(matterIdParam);
  const [matterStatus, setMatterStatus] = useState<string | null>(null);
  const [wizardData, setWizardData] = useState<DraftWizardData>(defaultWizardData);
  const [conflicts, setConflicts] = useState<DraftFactConflict[]>([]);
  const [allPartiesConfirmed, setAllPartiesConfirmed] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [hasGeneratedDraft, setHasGeneratedDraft] = useState(false);
  const [isExistingDraftDialogOpen, setIsExistingDraftDialogOpen] = useState(false);
  const [isLoadingMatter, setIsLoadingMatter] = useState(Boolean(matterIdParam));

  const { progress, isGenerating, error: generationError, generateDraft } = useDraftGeneration();

  const loadMatter = useCallback(async (id: string) => {
    const detail = await fetchDraftMatter(id);
    setMatterStatus(detail.status);
    setHasGeneratedDraft(draftMatterHasGeneratedDraft(detail));
    setWizardData((prev) => ({
      ...prev,
      templateId: detail.template_id ?? null,
      contractType: detail.contract_type ?? "",
      legalRoleId: detail.legal_role_id,
      jurisdiction: detail.jurisdiction ?? prev.jurisdiction,
      title: detail.title ?? "",
      backgroundInfo: detail.background_info ?? "",
      agentInstructions: detail.agent_instructions ?? "",
      sourceDocuments: (detail.source_documents ?? []).map(mapPersistedSourceDocument),
      parties: detail.parties.map((p) => ({
        label: p.label,
        legal_name: p.legal_name,
        entity_type: p.entity_type,
        registration_data: p.registration_data,
        address_data: p.address_data,
        signatory_data: p.signatory_data,
        source_ref: p.source_ref,
        confirmation_state: p.confirmation_state,
      })),
    }));
    setConflicts(detail.conflicts);
    setAllPartiesConfirmed(
      detail.parties.length > 0 &&
        detail.parties.every((p) => p.confirmation_state === "confirmed")
    );
  }, []);

  useEffect(() => {
    if (matterIdParam) {
      setMatterId(matterIdParam);
      setIsLoadingMatter(true);
      void loadMatter(matterIdParam)
        .catch((err) => {
          setPageError(err instanceof Error ? err.message : "Failed to load draft matter");
        })
        .finally(() => {
          setIsLoadingMatter(false);
        });
    }
  }, [matterIdParam, loadMatter]);

  useEffect(() => {
    const step = Number(searchParams.get("step") || "1");
    setCurrentStep(step >= 2 ? 2 : 1);
  }, [searchParams]);

  const updateField = useCallback(
    <K extends keyof DraftWizardData>(field: K, value: DraftWizardData[K]) => {
      setWizardData((prev) => ({ ...prev, [field]: value }));
      if (field === "parties") {
        const parties = value as DraftPartyInput[];
        setAllPartiesConfirmed(
          parties.length > 0 && parties.every((party) => party.confirmation_state === "confirmed")
        );
      }
    },
    []
  );

  const ensureMatter = useCallback(async () => {
    const instructionsOverLimit = instructionsExceedLimit(wizardData.agentInstructions);
    if (instructionsOverLimit) {
      setPageError(
        "Additional instructions are over 1,500 words and were not saved. Summarize them before generating."
      );
    }
    const agentInstructions = instructionsOverLimit ? undefined : wizardData.agentInstructions;
    if (matterId) {
      await updateDraftMatter(matterId, {
        template_id: wizardData.templateId ?? undefined,
        contract_type: wizardData.contractType || undefined,
        legal_role_id: wizardData.legalRoleId ?? undefined,
        jurisdiction: wizardData.jurisdiction,
        title: wizardData.title || undefined,
        background_info: wizardData.backgroundInfo,
        ...(agentInstructions !== undefined ? { agent_instructions: agentInstructions } : {}),
        status: currentStep === 1 ? "setup" : "background",
      });
      return matterId;
    }

    const created = await createDraftMatter({
      template_id: wizardData.templateId ?? undefined,
      contract_type: wizardData.contractType || undefined,
      legal_role_id: wizardData.legalRoleId ?? undefined,
      jurisdiction: wizardData.jurisdiction,
      title: wizardData.title || undefined,
      background_info: wizardData.backgroundInfo,
      ...(agentInstructions !== undefined ? { agent_instructions: agentInstructions } : {}),
      status: "setup",
    });
    setMatterId(created.id);
    return created.id;
  }, [matterId, wizardData, currentStep]);

  const handleSourceDocumentsChange = useCallback(
    async (next: DraftSourceDocumentInput[]) => {
      const prev = wizardData.sourceDocuments;
      updateField("sourceDocuments", next);

      const removed = prev.filter(
        (doc) => doc.persisted && !next.some((item) => item.id === doc.id)
      );
      const added = next.filter((doc) => doc.file && !doc.persisted);

      if (!removed.length && !added.length) {
        return;
      }

      setPageError(null);
      try {
        let id = matterId;
        if (!id) {
          id = await ensureMatter();
          setMatterId(id);
        }

        for (const doc of removed) {
          await deleteDraftSourceDocument(id, doc.id);
        }

        if (added.length) {
          const encoded = await encodeSourceDocuments(added);
          await uploadDraftSourceDocuments(id, encoded);
        }

        const detail = await fetchDraftMatter(id);
        updateField(
          "sourceDocuments",
          (detail.source_documents ?? []).map(mapPersistedSourceDocument)
        );
      } catch (err) {
        setPageError(err instanceof Error ? err.message : "Failed to save source documents");
        if (matterId) {
          await loadMatter(matterId);
        }
      }
    },
    [ensureMatter, loadMatter, matterId, updateField, wizardData.sourceDocuments]
  );

  const handleWizardChange = useCallback(
    <K extends keyof DraftWizardData>(field: K, value: DraftWizardData[K]) => {
      if (field === "sourceDocuments") {
        void handleSourceDocumentsChange(value as DraftSourceDocumentInput[]);
        return;
      }
      updateField(field, value);
    },
    [handleSourceDocumentsChange, updateField]
  );

  const handleStepChange = useCallback(
    async (step: number) => {
      setPageError(null);
      if (step < 1) {
        if (!matterId) {
          navigate("/drafting");
          return;
        }
        const { previousHref } = getDraftStepNav(matterId, 1, {
          matterStatus: matterStatus ?? undefined,
        });
        navigate(previousHref ?? "/drafting");
        return;
      }
      if (step === 2) {
        try {
          const id = await ensureMatter();
          navigate(`/drafting/new?matterId=${id}&step=2`, { replace: true });
          setCurrentStep(2);
        } catch (err) {
          setPageError(err instanceof Error ? err.message : "Failed to save draft setup");
        }
        return;
      }
      if (step === 1 && matterId) {
        navigate(`/drafting/new?matterId=${matterId}`, { replace: true });
        setCurrentStep(1);
        return;
      }
      setCurrentStep(step);
    },
    [ensureMatter, matterId, matterStatus, navigate]
  );

  const handleExtract = useCallback(
    async (body: { pasted_text?: string; document_text?: string }) => {
      setIsExtracting(true);
      setPageError(null);
      try {
        const id = await ensureMatter();
        const parties = await extractDraftParties(id, {
          ...body,
          background_info: wizardData.backgroundInfo,
        });
        const mapped: DraftPartyInput[] = parties.map((p) => ({
          label: p.label,
          legal_name: p.legal_name,
          entity_type: p.entity_type,
          registration_data: p.registration_data,
          address_data: p.address_data,
          signatory_data: p.signatory_data,
          source_ref: p.source_ref,
          confirmation_state: p.confirmation_state,
        }));
        updateField("parties", mapped);
        const detail = await fetchDraftMatter(id);
        setConflicts(detail.conflicts);
        setAllPartiesConfirmed(false);
      } catch (err) {
        setPageError(err instanceof Error ? err.message : "Party extraction failed");
      } finally {
        setIsExtracting(false);
      }
    },
    [ensureMatter, updateField, wizardData.backgroundInfo]
  );

  const handleConfirmParties = useCallback(async () => {
    if (!matterId) return;
    setIsConfirming(true);
    setPageError(null);
    try {
      const result = await confirmDraftParties(
        matterId,
        wizardData.parties.map((p) => ({
          label: p.label,
          legal_name: p.legal_name,
          entity_type: p.entity_type,
          registration_data: p.registration_data,
          address_data: p.address_data,
          signatory_data: p.signatory_data,
          source_ref: p.source_ref,
          confirmation_state: "confirmed",
        }))
      );
      updateField(
        "parties",
        result.parties.map((p) => ({
          label: p.label,
          legal_name: p.legal_name,
          entity_type: p.entity_type,
          registration_data: p.registration_data,
          address_data: p.address_data,
          signatory_data: p.signatory_data,
          source_ref: p.source_ref,
          confirmation_state: p.confirmation_state,
        }))
      );
      setAllPartiesConfirmed(result.all_confirmed);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to confirm parties");
    } finally {
      setIsConfirming(false);
    }
  }, [matterId, updateField, wizardData.parties]);

  const handleResolveConflict = useCallback(
    async (conflictId: string, value: string) => {
      if (!matterId) return;
      setIsResolvingConflict(true);
      try {
        await resolveDraftConflict(matterId, conflictId, value);
        const detail = await fetchDraftMatter(matterId);
        setConflicts(detail.conflicts);
      } catch (err) {
        setPageError(err instanceof Error ? err.message : "Failed to resolve conflict");
      } finally {
        setIsResolvingConflict(false);
      }
    },
    [matterId]
  );

  const handleGenerate = useCallback(async () => {
    setPageError(null);
    setIsExistingDraftDialogOpen(false);
    if (instructionsExceedLimit(wizardData.agentInstructions)) {
      setPageError("Instructions must be 1,500 words or fewer.");
      return;
    }
    try {
      const id = await ensureMatter();
      await updateDraftMatter(id, {
        background_info: wizardData.backgroundInfo,
        agent_instructions: wizardData.agentInstructions,
        status: "drafting",
      });
      const result = await generateDraft(id, {
        sourceDocuments: wizardData.sourceDocuments,
        backgroundInfo: wizardData.backgroundInfo,
        agentInstructions: wizardData.agentInstructions,
      });
      if (result) {
        navigate(`/drafting/${id}`, { replace: true });
      }
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to generate draft");
    }
  }, [ensureMatter, generateDraft, navigate, wizardData]);

  const handleContinueToExistingDraft = useCallback(async () => {
    if (!matterId) return;
    setPageError(null);
    setIsExistingDraftDialogOpen(false);
    if (instructionsExceedLimit(wizardData.agentInstructions)) {
      setPageError(
        "Additional instructions are over 1,500 words and were not saved. Summarize them before generating."
      );
      return;
    }
    try {
      await updateDraftMatter(matterId, {
        background_info: wizardData.backgroundInfo,
        agent_instructions: wizardData.agentInstructions,
      });
      navigate(`/drafting/${matterId}`);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to open draft");
    }
  }, [matterId, navigate, wizardData.agentInstructions, wizardData.backgroundInfo]);

  const combinedError = pageError || generationError;
  const showProgress = isGenerating || (progress.progress > 0 && !combinedError);

  const headerTitle = useMemo(
    () => (showProgress ? "Generating draft..." : "New draft matter"),
    [showProgress]
  );

  const isStep1Valid = useMemo(
    () => Boolean(wizardData.templateId && wizardData.legalRoleId && wizardData.jurisdiction),
    [wizardData.jurisdiction, wizardData.legalRoleId, wizardData.templateId]
  );

  const hasOpenConflicts = useMemo(
    () => conflicts.some((conflict) => conflict.status === "open"),
    [conflicts]
  );

  const canGenerateDraft = useMemo(
    () =>
      allPartiesConfirmed &&
      !hasOpenConflicts &&
      wizardData.parties.length > 0 &&
      !instructionsExceedLimit(wizardData.agentInstructions) &&
      !isGenerating &&
      !isLoadingMatter,
    [
      allPartiesConfirmed,
      hasOpenConflicts,
      isGenerating,
      isLoadingMatter,
      wizardData.agentInstructions,
      wizardData.parties.length,
    ]
  );

  const handleWizardContinue = useCallback(() => {
    setPageError(null);
    if (currentStep === 1) {
      if (!isStep1Valid) {
        setPageError("Select a template, legal role, and jurisdiction to continue.");
        return;
      }
      void handleStepChange(2);
      return;
    }
    if (hasGeneratedDraft) {
      setIsExistingDraftDialogOpen(true);
      return;
    }
    void handleGenerate();
  }, [currentStep, handleGenerate, handleStepChange, hasGeneratedDraft, isStep1Valid]);

  const wizardBackNav = useMemo(() => {
    if (!matterId) {
      return { href: "/drafting", title: "Back to drafts" };
    }
    const stepNav = getDraftStepNav(matterId, currentStep, {
      matterStatus: matterStatus ?? undefined,
    });
    return {
      href: stepNav.previousHref ?? "/drafting",
      title: stepNav.previousLabel ?? "Back to drafts",
    };
  }, [currentStep, matterId, matterStatus]);

  if (showProgress) {
    return (
      <div className={pageShellClassName}>
        <header className="border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-6 py-4 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80">
          <div className="flex items-center gap-4">
            <IconButton asChild title="Back to drafts">
              <Link to="/drafting">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </IconButton>
            <h1 className="text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              {headerTitle}
            </h1>
          </div>
        </header>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-2xl space-y-4">
            {combinedError ? (
              <ErrorAlert message={combinedError} />
            ) : null}
            <ProgressPanel progress={progress} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClassName}>
      <header className="min-w-0 shrink-0 border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-4 py-4 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80 md:px-6">
        <div className="mb-4 flex items-center gap-4">
          <IconButton asChild title={wizardBackNav.title}>
            <Link to={wizardBackNav.href}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </IconButton>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-violet-500/10 p-2">
              <PenLine className="h-5 w-5 text-violet-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                New Draft
              </h1>
              <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
                Configure your matter and generate an AI-assisted draft
              </p>
            </div>
          </div>
        </div>
        <DraftStepper currentStep={currentStep} matterId={matterId ?? undefined} />
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-8 md:px-6">
        <div className="mx-auto max-w-5xl">
          {combinedError ? (
            <div className="mb-6">
              <ErrorAlert message={combinedError} />
            </div>
          ) : null}
          <DraftWizard
            data={wizardData}
            conflicts={conflicts}
            allPartiesConfirmed={allPartiesConfirmed}
            currentStep={currentStep}
            onChange={handleWizardChange}
            onExtractFromPaste={(text) => handleExtract({ pasted_text: text })}
            onExtractFromFile={(text) => handleExtract({ document_text: text })}
            onConfirmParties={handleConfirmParties}
            onResolveConflict={handleResolveConflict}
            isExtracting={isExtracting}
            isConfirming={isConfirming}
            isResolvingConflict={isResolvingConflict}
          />
        </div>
      </main>

      <ConfirmDialog
        isOpen={isExistingDraftDialogOpen}
        onClose={() => setIsExistingDraftDialogOpen(false)}
        onCancel={() => void handleContinueToExistingDraft()}
        onConfirm={() => void handleGenerate()}
        title="Draft already generated"
        description="This matter already has a draft. Generate a new one from the current background, or continue to the existing draft?"
        cancelLabel="Continue to draft"
        confirmLabel="Generate again"
      />

      <DraftStepFooter
        currentStep={currentStep}
        matterId={matterId ?? undefined}
        matterStatus={matterStatus ?? undefined}
        backAction={
          currentStep === 1
            ? {
                label: "Back to drafts",
                onClick: () => void handleStepChange(0),
                disabled: isGenerating,
              }
            : {
                label: "Back to setup",
                onClick: () => void handleStepChange(1),
                disabled: isGenerating,
              }
        }
        primaryAction={
          currentStep === 1
            ? {
                label: "Continue to background",
                onClick: handleWizardContinue,
                showChevron: true,
              }
            : {
                label: isGenerating ? "Generating draft…" : "Generate draft",
                onClick: handleWizardContinue,
                disabled: !canGenerateDraft,
                loading: isGenerating,
              }
        }
      />
    </div>
  );
}
